import React, { useState, useCallback, lazy, Suspense } from 'react';
import { useEvents, Event } from './api/useEvents';
import { EventSeriesPanel } from './EventSeriesPanel';
import { Env, getAdminApiUrl, ENV_COLORS, Spinner, ErrorBanner, SuccessBanner } from '../shared';
import { formatInMarketTz } from '@/pages/Marketplace/shared/tzHelpers';

const CreateEventModal = lazy(() => import('./CreateEventModal'));
const EditEventModal = lazy(() => import('./EditEventModal'));
const CancelEventModal = lazy(() => import('./CancelEventModal'));

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatEventDate(startTime: string, tz: string | null): string {
  if (!tz) return new Date(startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return formatInMarketTz(startTime, tz, 'EEE, MMM d');
}

function formatEventTime(startTime: string, endTime: string | null, tz: string | null): string {
  if (!tz) {
    const start = new Date(startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (!endTime) return `${start} (TZ unset)`;
    const end = new Date(endTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${start} – ${end} (TZ unset)`;
  }
  const start = formatInMarketTz(startTime, tz, 'h:mm a');
  if (!endTime) return start;
  const end = formatInMarketTz(endTime, tz, 'h:mm a');
  return `${start} – ${end}`;
}

function StatusBadge({ status }: { status: Event['status'] }) {
  const styles = {
    upcoming: 'bg-violet-950/50 border-violet-800/50 text-violet-400',
    live: 'bg-emerald-950/50 border-emerald-800/50 text-emerald-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${styles[status]}`}>
      {status === 'live' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Events Tab
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

/** "Repeats weekly on Wed until 2026-12-31" — the tooltip behind the badge.
 *  recurrence_days is a JSON array stored in a varchar column, so it arrives as
 *  a string from the API but may already be an array in other callers. */
function recurrenceSummary(ev: {
  recurrence_pattern: string | null;
  recurrence_days?: string | string[] | null;
  series_end_date?: string | null;
}): string {
  const pattern = ev.recurrence_pattern || 'recurring';
  let days: string[] = [];
  const raw = ev.recurrence_days;
  if (Array.isArray(raw)) days = raw;
  else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) days = parsed;
    } catch {
      days = [];
    }
  }
  const dayText = days.length
    ? ` on ${days.map(d => d.slice(0, 1).toUpperCase() + d.slice(1, 3)).join(', ')}`
    : '';
  const until = ev.series_end_date ? ` until ${String(ev.series_end_date).slice(0, 10)}` : '';
  return `Repeats ${pattern}${dayText}${until}`;
}

export function EventsTab(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedEnv, setSelectedEnv] = useState<Env>('prod');
  const [page, setPage] = useState(0);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [cancelingEvent, setCancelingEvent] = useState<Event | null>(null);
  const [view, setView] = useState<'events' | 'series'>('events');
  // When set, the list shows only this series' occurrences.
  const [seriesFilter, setSeriesFilter] = useState<string | null>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeApiUrl = getAdminApiUrl(selectedEnv);

  const { events, total, loading, error, refetch } = useEvents({
    search: debouncedSearch,
    page,
    limit: PAGE_SIZE,
    apiUrl: activeApiUrl,
    seriesId: seriesFilter,
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  }, []);

  const handleEnvChange = useCallback((env: Env) => {
    setSelectedEnv(env);
    setPage(0);
  }, []);

  const showSuccess = useCallback((msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }, []);

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center gap-2 sm:gap-4 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[160px] max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search by title or venue…"
            className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
          />
        </div>
        {!loading && total > 0 && <span className="text-sm text-gray-500">{total} total</span>}

        {/* Env switcher */}
        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
          {(['dev', 'staging', 'prod'] as Env[]).map(env => {
            const envApiUrl = getAdminApiUrl(env);
            const isConfigured = Boolean(envApiUrl);
            const isActive = selectedEnv === env;
            return (
              <button
                key={env}
                onClick={() => isConfigured && handleEnvChange(env)}
                title={isConfigured ? undefined : 'Not configured'}
                disabled={!isConfigured}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  isActive
                    ? `${ENV_COLORS[env].pill} border-opacity-100`
                    : isConfigured
                      ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60'
                      : 'text-gray-600 border-transparent cursor-not-allowed opacity-40'
                }`}
              >
                {env}
              </button>
            );
          })}
        </div>

        {/* PROD badge */}
        {selectedEnv === 'prod' && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            PRODUCTION
          </span>
        )}

        <button
          onClick={() => {
            setView(v => (v === 'events' ? 'series' : 'events'));
            setSeriesFilter(null);
            setPage(0);
          }}
          title="Recurring event series and whether they are still producing"
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl border transition-all ${
            view === 'series'
              ? 'bg-violet-950/50 border-violet-700 text-violet-200'
              : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
          }`}
        >
          🔁 Series monitor
        </button>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl hover:opacity-90 transition-all"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          Create Event
        </button>
      </div>

      {successMsg && <SuccessBanner message={successMsg} />}
      {error && <ErrorBanner message={error} />}

      {view === 'series' && activeApiUrl && (
        <EventSeriesPanel
          apiUrl={activeApiUrl}
          search={debouncedSearch}
          onOpenSeries={(id) => {
            // Jump from a series row into its occurrences.
            setView('events');
            setPage(0);
            setSeriesFilter(id);
          }}
        />
      )}

      {view === 'events' && seriesFilter && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2.5 bg-violet-950/30 border border-violet-800/40 rounded-xl">
          <span className="text-sm text-violet-200">
            Showing occurrences of one series
          </span>
          <button
            onClick={() => { setSeriesFilter(null); setPage(0); }}
            className="ml-auto px-3 py-1 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white"
          >
            Clear
          </button>
        </div>
      )}

      {view === 'events' && (
      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        {/* Header row */}
        <div className="flex items-center gap-4 px-5 py-3 border-b border-gray-800">
          <div className="w-10 flex-shrink-0" />
          <p className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Event</p>
          <p className="hidden sm:block w-36 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date & Time</p>
          <p className="hidden sm:block w-24 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</p>
          <div className="flex-shrink-0 w-16" />
        </div>

        {loading && (
          <div className="py-16 text-center flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-gray-500">Loading events…</p>
          </div>
        )}
        {!loading && events.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-gray-500 text-sm">{search ? 'No events match your search.' : 'No upcoming events found.'}</p>
          </div>
        )}

        {!loading && events.map((ev, i) => (
          <div
            key={ev.id}
            className={`flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-800/50 active:bg-gray-800/50 transition-colors group ${i < events.length - 1 ? 'border-b border-gray-800' : ''}`}
          >
            {/* Thumbnail */}
            <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-700">
              {ev.image_url
                ? <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
                : <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>}
            </div>

            {/* Title + venue + mobile date/status */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <p className="text-sm font-medium text-white truncate">{ev.title}</p>
                {ev.is_recurring && (
                  <span
                    title={recurrenceSummary(ev)}
                    className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 bg-violet-950/40 border border-violet-800/50 rounded text-[10px] font-medium text-violet-300"
                  >
                    🔁 Recurring
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 truncate">{ev.business_name}</p>
              <div className="sm:hidden flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-xs text-gray-400">{formatEventDate(ev.start_time, ev.business_timezone)} · {formatEventTime(ev.start_time, ev.end_time, ev.business_timezone)}</span>
                <StatusBadge status={ev.status} />
              </div>
              <p className="text-xs text-gray-600 truncate font-mono hidden sm:block">{ev.id}</p>
            </div>

            {/* Date & time */}
            <div className="hidden sm:block w-36 flex-shrink-0">
              <p className="text-xs text-gray-300">{formatEventDate(ev.start_time, ev.business_timezone)}</p>
              <p className="text-xs text-gray-500">{formatEventTime(ev.start_time, ev.end_time, ev.business_timezone)}</p>
            </div>

            {/* Status */}
            <div className="hidden sm:flex w-24 flex-shrink-0">
              <StatusBadge status={ev.status} />
            </div>

            {/* Actions — always visible on mobile, hover-reveal on desktop */}
            <div className="flex items-center gap-1.5 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-all">
              <button
                onClick={() => setEditingEvent(ev)}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-violet-500 hover:bg-gray-700 active:bg-gray-700 transition-all"
              >
                Edit
              </button>
              <button
                onClick={() => setCancelingEvent(ev)}
                className="px-3 py-1.5 text-xs font-medium text-red-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-red-300 hover:border-red-700 hover:bg-red-950/30 active:bg-red-950/30 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Pagination */}
      {view === 'events' && !loading && total > 0 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-xs text-gray-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + events.length, total)} of {total} events
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                ← Prev
              </button>
              <span className="text-xs text-gray-500 tabular-nums">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <Suspense fallback={null}>
          <CreateEventModal
            onClose={() => setShowCreateModal(false)}
            onCreated={() => {
              setShowCreateModal(false);
              showSuccess('Event created successfully.');
              refetch();
            }}
          />
        </Suspense>
      )}

      {editingEvent && (
        <Suspense fallback={null}>
          <EditEventModal
            event={editingEvent}
            environment={selectedEnv}
            onClose={() => setEditingEvent(null)}
            onUpdated={updated => {
              setEditingEvent(null);
              showSuccess('Event updated successfully.');
              refetch();
            }}
          />
        </Suspense>
      )}

      {cancelingEvent && (
        <Suspense fallback={null}>
          <CancelEventModal
            event={cancelingEvent}
            environment={selectedEnv}
            onClose={() => setCancelingEvent(null)}
            onCancelled={() => {
              setCancelingEvent(null);
              showSuccess('Event cancelled successfully.');
              refetch();
            }}
          />
        </Suspense>
      )}
    </>
  );
}
